import { useState } from "react";
import { useUser } from "../../auth/UserContext.js";
import { Icon } from "../../components/Icon.js";
import { fmt } from "../../lib/money.js";
import type { KidBundle } from "../../api/me.js";

export default function Learn() {
  const { bundle } = useUser();
  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;

  const annualRate = kid.settings.interestRateAnnualBasisPoints / 10000;
  const monthlyRate = annualRate / 12;

  // Calculator: amount in dollars (slider 50–2000)
  const [amountDollars, setAmountDollars] = useState<number>(
    Math.max(50, Math.round(kid.account.balanceCents / 100)),
  );
  const [years, setYears] = useState(5);

  const finalDollars = amountDollars * Math.pow(1 + monthlyRate, years * 12);
  const interestEarnedDollars = finalDollars - amountDollars;

  return (
    <div>
      <h1 style={{ marginBottom: 6 }}>How banks help your money grow</h1>
      <p className="soft" style={{ marginBottom: 28 }}>
        A quick look at how interest works at KBank.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div className="card">
          <div className="row" style={{ marginBottom: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "var(--brand-soft)",
                color: "var(--brand)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="sparkle" size={22} />
            </div>
            <h3>1. Why does the bank pay you?</h3>
          </div>
          <p style={{ fontSize: 15, color: "var(--ink-soft)" }}>
            When you keep money in the bank, the bank uses it to help other people buy things like
            houses. As a thank-you, they pay you a little bit every month. That&apos;s called{" "}
            <b>interest</b>.
          </p>
        </div>

        <div className="card">
          <div className="row" style={{ marginBottom: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "oklch(0.95 0.04 45)",
                color: "oklch(0.55 0.12 45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="chart" size={22} />
            </div>
            <h3>2. The snowball effect</h3>
          </div>
          <p style={{ fontSize: 15, color: "var(--ink-soft)" }}>
            The more you have, the more interest you get each month. So your savings grow a little
            faster each month — like a snowball rolling down a hill.
          </p>
        </div>

        <div className="card" style={{ gridColumn: "1 / -1" }}>
          <h3 style={{ marginBottom: 16 }}>Try it out</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1.6fr",
              gap: 24,
              alignItems: "center",
            }}
          >
            <div>
              <label className="label">If you save</label>
              <div className="row" style={{ marginTop: 6 }}>
                <div style={{ fontSize: 28, fontFamily: "Fraunces, serif" }}>$</div>
                <input
                  className="input"
                  type="number"
                  value={amountDollars}
                  onChange={(e) => setAmountDollars(Number(e.target.value) || 0)}
                />
              </div>
              <input
                type="range"
                min="50"
                max="2000"
                step="10"
                value={amountDollars}
                onChange={(e) => setAmountDollars(Number(e.target.value))}
                style={{ width: "100%", marginTop: 10, accentColor: "var(--brand)" }}
              />
            </div>
            <div>
              <label className="label">For this many years</label>
              <div className="money money-md" style={{ marginTop: 6 }}>
                {years} {years === 1 ? "year" : "years"}
              </div>
              <input
                type="range"
                min="1"
                max="18"
                step="1"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                style={{ width: "100%", marginTop: 10, accentColor: "var(--brand)" }}
              />
            </div>
            <div style={{ background: "var(--brand-soft)", borderRadius: 20, padding: 22 }}>
              <div className="soft" style={{ fontSize: 13 }}>
                You&apos;d end up with
              </div>
              <div
                className="money money-lg"
                style={{ color: "var(--brand-deep)", margin: "4px 0 10px" }}
              >
                {fmt(Math.round(finalDollars * 100))}
              </div>
              <div className="chip pos">
                +{fmt(Math.round(interestEarnedDollars * 100))} just from interest
              </div>
            </div>
          </div>

          {/* visual bars */}
          <div style={{ marginTop: 24 }}>
            <div className="row" style={{ alignItems: "flex-end", gap: 10, height: 160 }}>
              {Array.from({ length: years + 1 }).map((_, i) => {
                const v = amountDollars * Math.pow(1 + monthlyRate, i * 12);
                const h = finalDollars > 0 ? (v / finalDollars) * 100 : 0;
                return (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--ink-mute)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      ${Math.round(v)}
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: `${h}%`,
                        background: "var(--brand)",
                        borderRadius: "8px 8px 0 0",
                        opacity: 0.3 + 0.7 * (i / Math.max(1, years)),
                      }}
                    />
                    <div style={{ fontSize: 11, color: "var(--ink-mute)" }}>y{i}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="row" style={{ marginBottom: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "oklch(0.95 0.04 155)",
                color: "oklch(0.50 0.12 155)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="piggy" size={22} />
            </div>
            <h3>3. Save first, spend what&apos;s left</h3>
          </div>
          <p style={{ fontSize: 15, color: "var(--ink-soft)" }}>
            A trick grown-ups use: as soon as you get money, put some toward a goal right away.
            What&apos;s left is what you can spend without feeling bad about it.
          </p>
        </div>

        <div className="card">
          <div className="row" style={{ marginBottom: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: "oklch(0.95 0.04 340)",
                color: "oklch(0.55 0.14 340)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="target" size={22} />
            </div>
            <h3>4. The &quot;wait a day&quot; rule</h3>
          </div>
          <p style={{ fontSize: 15, color: "var(--ink-soft)" }}>
            If you want to buy something that costs more than $10, wait one day and see if you still
            want it tomorrow. Most of the time, you don&apos;t — and your goals thank you.
          </p>
        </div>
      </div>
    </div>
  );
}
